import {useLegacyConfig} from '@aboutyou/config';
import {useRequest} from '@aboutyou/data-fetcher';
import {GetHelpCenterArticleResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/help_center/v1/service.types';
import {
  ArticleFromIRIS,
  SectionsFromIRIS,
  fetchDataFromIRIS,
  transformIrisArticleToTdrArticle,
} from '../utils/iris/iris';
import {
  useGenerateArticleCta,
  useGetBreadcrumbsForSection,
} from '../utils/iris/useGetTranslatedData';
import {useArticleIdFromUrl} from './useArticleIdFromUrl';

function useGetHelpCenterArticleBulk<
  Fragment extends keyof GetHelpCenterArticleResponse,
>(key?: Fragment) {
  const legacyConfig = useLegacyConfig();
  const currentArticleId = useArticleIdFromUrl();
  const generateArticleCta = useGenerateArticleCta();
  const getBreadcrumbsForSection = useGetBreadcrumbsForSection();

  return useRequest<
    | GetHelpCenterArticleResponse
    | Record<Fragment, GetHelpCenterArticleResponse[Fragment]>
  >(
    `HELP_CENTER_SSF_ARTICLE-${currentArticleId}-${key ?? 'BULK'}`,
    async () => {
      const [{sections}, {article}] = await Promise.all([
        fetchDataFromIRIS<{sections: SectionsFromIRIS}>(
          '/sections',
          legacyConfig.help_center_GET,
        ),
        fetchDataFromIRIS<{article: ArticleFromIRIS}>(
          `/articles/${currentArticleId}`,
          legacyConfig.help_center_GET,
        ),
      ]);

      // For articles breadcrumbs stop at the section parent, not the section of the article itself
      const parentSectionId = sections.find(
        section => section.id === article.section_id,
      )?.parent_section_id;

      const response = {
        article: transformIrisArticleToTdrArticle(article),
        breadcrumbs: getBreadcrumbsForSection(
          sections,
          parentSectionId ?? article.section_id,
        ),
        ctaLink: generateArticleCta(article.section_id),
      };

      if (key == null) {
        return response;
      }

      return {[key as Fragment]: response[key]} as Record<
        Fragment,
        GetHelpCenterArticleResponse[Fragment]
      >;
    },
  );
}

export function useGetHelpCenterArticle() {
  return useGetHelpCenterArticleBulk('article');
}

export function useGetHelpCenterArticleBreacrumbs() {
  return useGetHelpCenterArticleBulk('breadcrumbs');
}

export function useGetHelpCenterArticleCTA() {
  return useGetHelpCenterArticleBulk('ctaLink');
}
